import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { vendorService } from '../../services/dataService';
import styles from './SettingsModalitiesPage.module.css';

const CATEGORY_OPTIONS = [
  { value: 'modality', label: 'Modality' },
  { value: 'facility_hmis', label: 'Facility HMIS' },
];

const CUSTOM_CATEGORY_VALUE = '__custom__';

const initialCreateForm = {
  category: 'modality',
  name: '',
  code: '',
  status: 'active',
};

const initialEditForm = {
  category: 'modality',
  name: '',
  code: '',
  status: 'active',
};

const normalizeApiError = (error, fallback) => (
  error?.response?.data?.detail || error?.detail || fallback
);

const formatCategoryLabel = (value) => String(value || '')
  .split('_')
  .filter(Boolean)
  .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
  .join(' ');

const SettingsModalitiesPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('modality');
  const [nameFilter, setNameFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [createForm, setCreateForm] = useState(initialCreateForm);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createCustomCategory, setCreateCustomCategory] = useState('');
  const [editId, setEditId] = useState('');
  const [editForm, setEditForm] = useState(initialEditForm);
  const [editCustomCategory, setEditCustomCategory] = useState('');

  const categoryOptions = useMemo(() => {
    const values = new Set([
      ...CATEGORY_OPTIONS.map((option) => option.value),
      ...items.map((item) => item.category).filter(Boolean),
      createForm.category,
      editForm.category,
      categoryFilter,
    ].filter(Boolean));

    return Array.from(values).map((value) => ({
      value,
      label: CATEGORY_OPTIONS.find((option) => option.value === value)?.label || formatCategoryLabel(value),
    }));
  }, [items, createForm.category, editForm.category, categoryFilter]);

  const filteredItems = useMemo(() => {
    const term = nameFilter.trim().toLowerCase();

    return items.filter((item) => {
      if (!term) return true;

      return [item.name, item.code, item.category]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term));
    });
  }, [items, nameFilter]);

  const loadDropdownOptions = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await vendorService.getDropdownConfig({
        category: categoryFilter || undefined,
        status: statusFilter || undefined,
      });
      setItems(response.items || []);
    } catch (err) {
      setError(normalizeApiError(err, 'Failed to load dropdown options'));
    } finally {
      setLoading(false);
    }
  }, [categoryFilter, statusFilter]);

  useEffect(() => {
    loadDropdownOptions();
  }, [loadDropdownOptions]);

  useEffect(() => {
    setCreateForm((value) => ({
      ...value,
      category: categoryFilter || value.category || initialCreateForm.category,
    }));
    setCreateCustomCategory('');
  }, [categoryFilter]);

  const resolveCreateCategory = () => {
    const categoryValue = createForm.category === CUSTOM_CATEGORY_VALUE
      ? createCustomCategory.trim().toLowerCase().replace(/\s+/g, '_')
      : createForm.category;

    return categoryValue;
  };

  const resolveEditCategory = () => {
    const categoryValue = editForm.category === CUSTOM_CATEGORY_VALUE
      ? editCustomCategory.trim().toLowerCase().replace(/\s+/g, '_')
      : editForm.category;

    return categoryValue;
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    const resolvedCategory = resolveCreateCategory();
    if (!resolvedCategory) {
      setError('Category is required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await vendorService.createDropdownConfig({
        ...createForm,
        category: resolvedCategory,
        code: createForm.code.toUpperCase(),
      });
      setCreateForm({ ...initialCreateForm, category: resolvedCategory });
      setCreateCustomCategory('');
      setShowCreateModal(false);
      await loadDropdownOptions();
    } catch (err) {
      setError(normalizeApiError(err, 'Failed to create dropdown option'));
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (item) => {
    setEditId(item.id);
    setEditCustomCategory('');
    setEditForm({
      category: item.category || categoryFilter,
      name: item.name || '',
      code: item.code || '',
      status: (item.status || 'active').toLowerCase(),
    });
  };

  const cancelEdit = () => {
    setEditId('');
    setEditCustomCategory('');
    setEditForm({
      ...initialEditForm,
      category: categoryFilter || initialEditForm.category,
    });
  };

  const handleUpdate = async (event) => {
    event.preventDefault();
    if (!editId) return;
    const resolvedCategory = resolveEditCategory();
    if (!resolvedCategory) {
      setError('Category is required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await vendorService.updateDropdownConfig(editId, {
        ...editForm,
        category: resolvedCategory,
        code: editForm.code.toUpperCase(),
      });
      cancelEdit();
      await loadDropdownOptions();
    } catch (err) {
      setError(normalizeApiError(err, 'Failed to update dropdown option'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1>Dropdown Configuration</h1>
        <div className={styles.headerActions}>
          <Link className={styles.backLink} to="/settings">Back to Settings</Link>
          <button type="button" className={styles.addButton} onClick={() => setShowCreateModal(true)}>
            Add Option
          </button>
        </div>
      </header>

      <section className={styles.panel}>
        <h2>Filters</h2>
        <div className={styles.filterRow}>
          <select value={categoryFilter} onChange={(event) => setCategoryFilter(event.target.value)}>
            <option value="">All categories</option>
            {categoryOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          <input
            placeholder="Search by name, code, category"
            value={nameFilter}
            onChange={(event) => setNameFilter(event.target.value)}
          />
          <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
            <option value="">All statuses</option>
            <option value="active">active</option>
            <option value="disabled">disabled</option>
          </select>
        </div>
      </section>

      {!showCreateModal && !editId && error ? <div className={styles.error}>{error}</div> : null}

      <section className={styles.panel}>
        <h2>Dropdown Option List</h2>
        {loading ? <p>Loading dropdown options...</p> : null}
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Category</th>
                <th>Name</th>
                <th>Code</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((item) => (
                <tr key={item.id}>
                  <td>{item.category}</td>
                  <td>{item.name}</td>
                  <td>{item.code}</td>
                  <td>{String(item.status || '').toLowerCase()}</td>
                  <td className={styles.actions}>
                    <button type="button" onClick={() => startEdit(item)}>Edit</button>
                  </td>
                </tr>
              ))}
              {!loading && filteredItems.length === 0 ? (
                <tr>
                  <td colSpan={5}>No dropdown options found for selected category.</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>

      {showCreateModal ? (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h2>Create Dropdown Option</h2>
              <button type="button" onClick={() => setShowCreateModal(false)}>
                Close
              </button>
            </div>
            {error ? <div className={styles.error}>{error}</div> : null}
            <form onSubmit={handleCreate} className={styles.grid3}>
              <select
                value={createForm.category}
                onChange={(event) => {
                  const nextValue = event.target.value;
                  setCreateForm((value) => ({ ...value, category: nextValue }));
                  if (nextValue !== CUSTOM_CATEGORY_VALUE) {
                    setCreateCustomCategory('');
                  }
                }}
              >
                {categoryOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
                <option value={CUSTOM_CATEGORY_VALUE}>Add new category</option>
              </select>
              {createForm.category === CUSTOM_CATEGORY_VALUE ? (
                <input
                  required
                  placeholder="New category (e.g vendor_type)"
                  value={createCustomCategory}
                  onChange={(event) => setCreateCustomCategory(event.target.value)}
                />
              ) : null}
              <input
                required
                placeholder="Name"
                value={createForm.name}
                onChange={(event) => setCreateForm((value) => ({ ...value, name: event.target.value }))}
              />
              <input
                required
                placeholder="Code (e.g CT)"
                maxLength={8}
                value={createForm.code}
                onChange={(event) => setCreateForm((value) => ({ ...value, code: event.target.value.toUpperCase() }))}
              />
              <select
                value={createForm.status}
                onChange={(event) => setCreateForm((value) => ({ ...value, status: event.target.value }))}
              >
                <option value="active">active</option>
                <option value="disabled">disabled</option>
              </select>
              <div className={styles.inlineActions}>
                <button type="submit" disabled={saving}>{saving ? 'Saving...' : 'Create Option'}</button>
                <button type="button" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {editId ? (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h2>Update Dropdown Option</h2>
              <button type="button" onClick={cancelEdit}>
                Close
              </button>
            </div>
            {error ? <div className={styles.error}>{error}</div> : null}
            <form onSubmit={handleUpdate} className={styles.grid3}>
              <select
                value={editForm.category}
                onChange={(event) => {
                  const nextValue = event.target.value;
                  setEditForm((value) => ({ ...value, category: nextValue }));
                  if (nextValue !== CUSTOM_CATEGORY_VALUE) {
                    setEditCustomCategory('');
                  }
                }}
              >
                {categoryOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
                <option value={CUSTOM_CATEGORY_VALUE}>Add new category</option>
              </select>
              {editForm.category === CUSTOM_CATEGORY_VALUE ? (
                <input
                  required
                  placeholder="New category"
                  value={editCustomCategory}
                  onChange={(event) => setEditCustomCategory(event.target.value)}
                />
              ) : null}
              <input
                required
                placeholder="Name"
                value={editForm.name}
                onChange={(event) => setEditForm((value) => ({ ...value, name: event.target.value }))}
              />
              <input
                required
                placeholder="Code"
                maxLength={8}
                value={editForm.code}
                onChange={(event) => setEditForm((value) => ({ ...value, code: event.target.value.toUpperCase() }))}
              />
              <select
                value={editForm.status}
                onChange={(event) => setEditForm((value) => ({ ...value, status: event.target.value }))}
              >
                <option value="active">active</option>
                <option value="disabled">disabled</option>
              </select>
              <div className={styles.inlineActions}>
                <button type="submit" disabled={saving}>{saving ? 'Saving...' : 'Update Option'}</button>
                <button type="button" onClick={cancelEdit}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default SettingsModalitiesPage;
